import React, {Component} from 'react';
import PropTypes from "prop-types";
import _ from 'lodash';

import Table from 'react-bootstrap/Table';
import Button from 'react-bootstrap/Button';
import TableCell from "./TableCell";
import TableRow from "./TableRow";
import TableHeader from "./TableHeader";

import {COLUMN_SHAPE} from "../../utilities/columns";
import {TEXT} from "../../utilities/strings";

const EXPANSION_LIMIT = 12;

/**
 * Component to render the detailed table used in State, County, and Summary sections
 *
 * @example
 * <DetailTable
 *   rowKey={VARIABLES.StateID}
 *   data={this.props.statesData}
 *   loading={this.props.loading}
 *   columns={Utils.getDetailColumns(this.props.selectedCost)}
 *   selectedRows={[this.props.selectedStateFips, this.props.comparisonStateFips]}
 *   onSelect={this.props.onSelectedStateChange}
 *   onComparison={this.props.onComparisonStateChange}
 *  />
 */
export default class DetailTable extends Component {

  static propTypes = {
    focusId: PropTypes.string,
    caption: PropTypes.string,
    data: PropTypes.array,
    responsive: PropTypes.bool,
    columns: PropTypes.arrayOf(COLUMN_SHAPE).isRequired,
    rowKey: PropTypes.string.isRequired,
    selectedRowId: PropTypes.number,
    comparisonRowId: PropTypes.number,
    selectedRows: PropTypes.array,
    darkHeader: PropTypes.bool,
    loading: PropTypes.bool,
    bordered: PropTypes.bool,
    striped: PropTypes.bool,
    hover: PropTypes.bool,
    expandable: PropTypes.bool,
    onSelect: PropTypes.func,
    onComparison: PropTypes.func
  };

  static defaultProps = {
    expandable: false,
    responsive: true,
    darkHeader: true,
    striped: true,
    hover: true,
    size: 'sm',
  };

  state = {
    expanded: !this.props.expandable
  }

  /**
   *  Logic to determine if the Show More button should be rendered
   */
  shouldShowExpansion = () => {
    return this.props.expandable && _.size(this.props.data) > EXPANSION_LIMIT;
  }

  getRowExpansionLimit = () => {
    return this.state.expanded ? Number.MAX_VALUE : EXPANSION_LIMIT;
  }

  renderRow = (row, cols, rowIndex) => {
    const key = _.get(row, this.props.rowKey);
    const isSelected = key ===  this.props.selectedRowId;
    const isComparison = key === this.props.comparisonRowId;

    const isFaded = this.shouldShowExpansion() && rowIndex + 1 === this.getRowExpansionLimit();

    const cells = _.map(cols, col => (
      <TableCell
        key={key + col.field}
        column={col}
        value={row}
        isSelected={isSelected}
        isComparison={isComparison}
      />
    ));

    return (
      <TableRow
        key={key}
        value={key}
        faded={isFaded}
        highlighted={isSelected || isComparison}
        onSelect={this.props.onSelect}
        onComparison={this.props.onComparison}
        children={cells}
      />
    )
  };

  renderBody = () => {
    const limitedRows = _.take(this.props.data, this.getRowExpansionLimit())
    return _.map(limitedRows, (row, rowIndex) => this.renderRow(row, this.props.columns, rowIndex));
  };

  render() {
    return (
      <div>
        <Table
          size="sm"
          tabindex="0"
          id={this.props.focusId}
          className={this.props.hover ? 'table-hover-light' : ''}
          hover={this.props.hover}
          bordered={this.props.bordered}
          striped={this.props.striped}
          responsive={this.props.responsive}>

          <caption className="caption-bottom">{this.props.caption}</caption>

          <thead className={this.props.darkHeader ? "thead-dark" : ""}>
          <TableHeader columns={this.props.columns}/>
          </thead>

          <tbody>
          {this.renderBody()}
          </tbody>

        </Table>

        {this.shouldShowExpansion() && (
          <Button size="sm" className="expander w-100 mb-3"
                  onClick={() => this.setState({expanded: !this.state.expanded})}>
            {this.state.expanded ? TEXT.table_collapse : TEXT.table_expand}
          </Button>
        )}

      </div>
    );
  }
}
